/**
 * Session expiry handling (DOP-267)
 *
 * A 401 used to hard-navigate to /login mid-action, discarding whatever the
 * user was doing with no explanation. This module holds the small pieces that
 * make that transition legible:
 * - which endpoints must NOT be treated as an expired session (bad credentials
 *   on the login/signup forms are ordinary form errors),
 * - a one-shot "your session expired" notice the login page reads,
 * - the `?next=` round-trip so re-login returns the user where they were.
 */

/** sessionStorage flag set on redirect, consumed once by the login page. */
export const SESSION_EXPIRED_STORAGE_KEY = "doppler.session_expired";

/** Query param carrying the path to return to after re-login. */
export const RETURN_TO_PARAM = "next";

export const SESSION_EXPIRED_MESSAGE =
	"Your session has expired — please log in again.";

/**
 * Thrown by `apiFetch` when a request is rejected for an expired session.
 *
 * The distinction matters to recovery code: a 401 means the request never
 * authenticated, so the backend certainly did not commit anything. Any OTHER
 * failure is ambiguous — the write may well have landed.
 */
export class SessionExpiredError extends Error {
	constructor(message: string = SESSION_EXPIRED_MESSAGE) {
		super(message);
		this.name = "SessionExpiredError";
		// Subclassing Error across the TS 4.9 / ES5 downlevel needs the
		// prototype restored by hand, or `instanceof` is false.
		Object.setPrototypeOf(this, SessionExpiredError.prototype);
	}
}

export const isSessionExpiredError = (error: unknown): boolean =>
	error instanceof SessionExpiredError;

/**
 * Auth endpoints answer 401 for *bad credentials*, not for an expired session.
 * Those responses must reach the form so it can show its own error.
 */
const AUTH_ENDPOINT_MARKERS = [
	"/auth/jwt/",
	"/auth/register",
	"/auth/forgot-password",
	"/auth/reset-password",
	// A spent or expired support link (DOP-562): the entry page says so itself.
	"/support/sessions/exchange",
];

export const isAuthEndpoint = (endpoint: string): boolean =>
	AUTH_ENDPOINT_MARKERS.some((marker) => endpoint.includes(marker));

/** Pages where "you were kicked out of what you were doing" does not apply. */
const AUTH_PAGE_PATHS = [
	"/support/",
	"/login",
	"/signup",
	"/forgot-password",
	"/reset-password",
	"/auth/",
];

const isOnAuthPage = (pathname: string): boolean =>
	AUTH_PAGE_PATHS.some((path) => pathname.startsWith(path));

/**
 * Accept only same-origin absolute paths. A raw `?next=` is attacker-supplied,
 * so "//evil.com", "https://evil.com" and backslash tricks such as
 * "/\\evil.com" — which URL parsing normalises to "//evil.com" — must not
 * become a redirect target. The value is parsed rather than pattern-matched,
 * and only the normalised path is handed back.
 */
export const safeReturnPath = (raw: string | null | undefined): string | null => {
	if (!raw) return null;
	if (!raw.startsWith("/") || raw.startsWith("//")) return null;
	// Backslashes and control characters are the classic normalisation tricks:
	// browsers and routers fold "\\" into "//" and strip C0 bytes, which turns a
	// path that looks relative into an off-origin URL.
	// eslint-disable-next-line no-control-regex
	if (/[\\\u0000-\u001F\u007F]/.test(raw)) return null;

	let parsed: URL;
	try {
		parsed = new URL(raw, window.location.origin);
	} catch {
		return null;
	}
	if (parsed.origin !== window.location.origin) return null;

	const normalized = `${parsed.pathname}${parsed.search}${parsed.hash}`;
	if (!normalized.startsWith("/") || normalized.startsWith("//")) return null;
	if (isOnAuthPage(parsed.pathname)) return null;
	return normalized;
};

export const currentReturnPath = (): string | null =>
	safeReturnPath(
		`${window.location.pathname}${window.location.search}${window.location.hash}`,
	);

export const buildLoginUrl = (returnTo: string | null): string =>
	returnTo
		? `/login?${RETURN_TO_PARAM}=${encodeURIComponent(returnTo)}`
		: "/login";

// Several requests can 401 in the same tick (a page fans out on load); only the
// first one should drive the navigation.
let redirectInFlight = false;

/** Test seam — the module-level guard would otherwise leak between cases. */
export const resetSessionExpiryGuard = (): void => {
	redirectInFlight = false;
};

export const markSessionExpired = (): void => {
	try {
		window.sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, "1");
	} catch {
		// Private-mode browsers can throw on sessionStorage; the redirect still
		// has to happen, the user just loses the explanatory banner.
	}
};

/** Reads the notice and clears it, so a later manual visit stays clean. */
export const consumeSessionExpiredNotice = (): boolean => {
	try {
		const flagged =
			window.sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY) === "1";
		if (flagged) {
			window.sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
		}
		return flagged;
	} catch {
		return false;
	}
};

/**
 * Send the user to the login page with an explanation and a way back.
 * `replace` (not `href`) keeps the dead, unauthenticated page out of history.
 */
/** Where a support session (DOP-562) lands when its token stops working. */
export const SUPPORT_ENDED_PATH = "/support/ended";

/**
 * A support session that 401s has ended or run out its 30 minutes. The
 * operator is not sent to the customer's login page: they have no account
 * here, and the tab's job is over.
 */
export const redirectToSupportEnded = (): void => {
	if (redirectInFlight) return;
	redirectInFlight = true;
	window.location.replace(SUPPORT_ENDED_PATH);
};

export const redirectToLoginForExpiredSession = (): void => {
	if (redirectInFlight) return;
	redirectInFlight = true;

	markSessionExpired();
	const returnTo = isOnAuthPage(window.location.pathname)
		? null
		: currentReturnPath();
	window.location.replace(buildLoginUrl(returnTo));
};
